import { DialogTitle } from "@mui/material";
import { stellaInspectionNoteBusiness } from "api/bussiness/inspection-note/stella-inspection-note/stella-inspection-note-business";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { workOrderBusiness } from "api/bussiness/work-order/work-order-bussiness";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { InspectionConclusionEnum } from "commons/enum/inspection";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IUpdateInspectionStellaNote } from "mobx-stores/_models/inspection-note/stella-inspection-note/stella-inspection-note";
import { IUpdateStellaInspectionNoteItem } from "mobx-stores/_models/inspection-note/stella-inspection-note/stella-inspection-note-item";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { IWorkOrderProperties } from "mobx-stores/_models/work-order/work-order";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditInspectionNoteFormSchema } from "../schema";
import { conclusionDefautText, createQuantification } from "../share";

export const EditStellaInspectionNote = observer(() => {
  const {
    stellaInspectionNoteManagement: {
      openEdit,
      getStellaInspectionNoteDetail,
      setProp,
      inspectionNoteDetail,
    },
    setProp: setRootStoreProp,
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
  } = useForm<IUpdateInspectionStellaNote>(
    (form) => getEditInspectionNoteFormSchema(form),
    {
      id: inspectionNoteDetail?.id,
      expireDate: inspectionNoteDetail?.expireDate || "",
      inspectionDate: inspectionNoteDetail?.inspectionDate || "",
      manufacturingDate: inspectionNoteDetail?.manufacturingDate || "",
      number: inspectionNoteDetail?.number || "",
      workOrderId: inspectionNoteDetail?.workOrderId || "",
      workOrderPOId: inspectionNoteDetail?.workOrderPOId || "",
      conclusion: conclusionDefautText,
      customerId: "",
      items:
        inspectionNoteDetail?.items?.map((x) => ({
          id: x.id,
          amount: x.amount || "",
          productId: x.productId || "",
          appearance: "",
          appearanceConclusion: InspectionConclusionEnum.None,
          appearanceResult: "",
          foilResult: 0,
          foilConclusion: InspectionConclusionEnum.None,
          height: 0,
          heightConclusion: InspectionConclusionEnum.None,
          heightResult: 0,
          length: 0,
          lengthConclusion: InspectionConclusionEnum.None,
          lengthResult: 0,
          quantification: "",
          quantificationConclusion: InspectionConclusionEnum.None,
          quantificationResult: 0,
          width: 0,
          widthConclusion: InspectionConclusionEnum.None,
          widthResult: 0,
          foil: "",
          printPaperSP: 0,
          printPaperSPUnit: x.printPaperSPUnit || "",
          foldingStandardSizeConclusion: InspectionConclusionEnum.None,
          foldingStandardSizeResult: "",
          foldingStandardCountConclusion: InspectionConclusionEnum.None,
          foldingStandardCountResult: "",
        })) || [],
    },
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const setCustomerSelectionIsEmty = useCallback(() => {
    const customerIdCtr = getControl("customerId");
    customerIdCtr.setProp("optionItems", []);
    customerIdCtr.setProp("value", "");
  }, [getControl]);

  const setOptionItemsForCustomer = useCallback(
    (wo: IWorkOrderPoProperties) => {
      const customerIdCtr = getControl("customerId");
      customerIdCtr.setProp("optionItems", [
        {
          value: wo.customer?.id,
          label: wo.customer?.dealingName || "",
        },
      ]);
      customerIdCtr.setProp("value", wo.customer?.id);
    },
    [getControl],
  );

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        productIdSchema.optionItems = wo.items?.map((x) => ({
          label: createProductOptionLabel(x.product as IProductProperties),
          value: x.product?.id,
        }));

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl],
  );

  const setOptionItemsForProductFromWorkOrderPo = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl],
  );

  const setOptionItemsSchemaForProduct = useCallback(async () => {
    if (!inspectionNoteDetail) return;

    const hasWorkOrder = !!inspectionNoteDetail.workOrderId;
    const hasWorkOrderPO = !!inspectionNoteDetail.workOrderPOId;
    if (!hasWorkOrder && !hasWorkOrderPO) return;

    if (hasWorkOrderPO) {
      const woDetail = await workOrderPoBusiness.getById(
        inspectionNoteDetail.workOrderPOId!,
      );
      setOptionItemsForProductFromWorkOrderPo(woDetail);

      if (inspectionNoteDetail.items && inspectionNoteDetail.items.length > 0) {
        const product = woDetail.purchaseOrderItem?.product;
        const productOption = product
          ? [
              {
                value: product.id,
                label: createProductOptionLabel(product as IProductProperties),
              },
            ]
          : [];
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = inspectionNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", productOption);
          }
        });
      }
      return;
    }

    if (hasWorkOrder) {
      const woDetail = await workOrderBusiness.getById(
        inspectionNoteDetail.workOrderId!,
      );
      setOptionItemsForProduct(woDetail);

      if (inspectionNoteDetail.items && inspectionNoteDetail.items.length > 0) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = inspectionNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp(
              "optionItems",
              woDetail.items?.map((x) => ({
                value: x.product?.id,
                label: createProductOptionLabel(x.product as IProductProperties),
              })),
            );
          }
        });
      }
    }
  }, [getControl, inspectionNoteDetail, setOptionItemsForProduct, setOptionItemsForProductFromWorkOrderPo]);

  useEffect(() => {
    if (inspectionNoteDetail) {
      setDefaults({
        id: inspectionNoteDetail.id,
        expireDate: inspectionNoteDetail?.expireDate || "",
        inspectionDate: inspectionNoteDetail?.inspectionDate || "",
        manufacturingDate: inspectionNoteDetail?.manufacturingDate || "",
        number: inspectionNoteDetail?.number || "",
        workOrderId: inspectionNoteDetail?.workOrderId || "",
        workOrderPOId: inspectionNoteDetail?.workOrderPOId || "",
        conclusion: inspectionNoteDetail.conclusion || conclusionDefautText,
        customerId: inspectionNoteDetail.customerId || "",
        items: inspectionNoteDetail.items.map((x) => ({
          id: x.id,
          amount: x.amount || "",
          productId: x.productId || "",
          appearanceConclusion:
            x.appearanceConclusion || InspectionConclusionEnum.None,
          appearanceResult: x.appearanceResult || "",
          appearance: x.appearance || "",
          foilResult: x.foilResult || 0,
          foilConclusion: x.foilConclusion || InspectionConclusionEnum.None,
          height: x.product?.dimensionWeight?.heightStandard || 0,
          heightConclusion: x.heightConclusion || InspectionConclusionEnum.None,
          heightResult: x.heightResult || 0,
          length: x.product?.dimensionWeight?.lengthStandard || 0,
          lengthConclusion: x.lengthConclusion || InspectionConclusionEnum.None,
          lengthResult: x.lengthResult || 0,
          quantification: (x.product && createQuantification(x.product)) || "",
          quantificationConclusion:
            x.quantificationConclusion || InspectionConclusionEnum.None,
          quantificationResult: x.quantificationResult || 0,
          width: x.product?.dimensionWeight?.widthStandard || 0,
          widthConclusion: x.widthConclusion || InspectionConclusionEnum.None,
          widthResult: x.widthResult || 0,
          foil: x.product?.process?.foil || "",
          printPaperSP: x.product?.process?.printPaperSP || 0,
          printPaperSPUnit: x.printPaperSPUnit || "",
          foldingStandardSizeConclusion:
            x.foldingStandardSizeConclusion || InspectionConclusionEnum.None,
          foldingStandardSizeResult: x.foldingStandardSizeResult || "",
          foldingStandardCountConclusion:
            x.foldingStandardCountConclusion || InspectionConclusionEnum.None,
          foldingStandardCountResult: x.foldingStandardCountResult || "",
          foldingStandardSize:
            x.product?.dimensionWeight?.foldingStandardSize || "",
          foldingStandardCount:
            x.product?.dimensionWeight?.foldingStandardCount || "",
        })),
      });

      setOptionItemsSchemaForProduct();
    }
  }, [
    getControl,
    inspectionNoteDetail,
    setDefaults,
    setOptionItemsSchemaForProduct,
  ]);

  useEffect(() => {
    const woPoCtr = getControl("workOrderPOId");
    woPoCtr.setProp("optionItems", [
      {
        value: inspectionNoteDetail?.workOrderPOId,
        label: inspectionNoteDetail?.workOrderPO?.woNumber || "",
      },
    ]);

    const customerCtr = getControl("customerId");
    customerCtr.setProp("optionItems", [
      {
        value: inspectionNoteDetail?.customerId,
        label: `${inspectionNoteDetail?.customer?.dealingName || ""}`,
      },
    ]);
  }, [
    getControl,
    inspectionNoteDetail?.customer?.dealingName,
    inspectionNoteDetail?.customerId,
    inspectionNoteDetail?.workOrderPO?.woNumber,
    inspectionNoteDetail?.workOrderPOId,
    inspectionNoteDetail?.workOrder?.woNumber,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateInspectionStellaNote) => {
      try {
        if (!inspectionNoteDetail?.id) return;
        await stellaInspectionNoteBusiness.update(
          inspectionNoteDetail?.id,
          data,
        );
        await getStellaInspectionNoteDetail(inspectionNoteDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getStellaInspectionNoteDetail, inspectionNoteDetail?.id, setProp],
  );

  const inspectionNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "manufacturingDate") {
        if (control.value && dayjs(control.value).isValid()) {
          getControl("expireDate").setProp(
            "value",
            control.value.add(3, "year"),
          );
        } else {
          getControl("expireDate").setProp("value", "");
        }
      }
      if (control.name === "workOrderPOId") {
        try {
          setRootStoreProp("appFreeze", true);
          if (control.value) {
            cleanItems();
            setOptionItemsForProductIsEmty();
            setCustomerSelectionIsEmty();
            const wo = await workOrderPoBusiness.getById(control.value);
            setOptionItemsForCustomer(wo);
            setOptionItemsForProductFromWorkOrderPo(wo);

            const woProductId = wo.purchaseOrderItem?.productId;
            if (woProductId) {
              const product = await productBussiness.getById(woProductId);
              itemControl.addChildren<IUpdateStellaInspectionNoteItem>([
                {
                  productId: woProductId,
                  amount: "",
                  length: product?.dimensionWeight?.lengthStandard || 0,
                  lengthResult: 0,
                  appearanceConclusion: InspectionConclusionEnum.None,
                  appearanceResult: "",
                  height: product?.dimensionWeight?.heightStandard || 0,
                  heightResult: 0,
                  heightConclusion: InspectionConclusionEnum.None,
                  width: product?.dimensionWeight?.widthStandard || 0,
                  widthResult: 0,
                  widthConclusion: InspectionConclusionEnum.None,
                  foil: product.process?.foil || "",
                  foilResult: 0,
                  foilConclusion: InspectionConclusionEnum.None,
                  lengthConclusion: InspectionConclusionEnum.None,
                  quantification: createQuantification(product),
                  quantificationConclusion: InspectionConclusionEnum.None,
                  quantificationResult: 0,
                  printPaperSP: product.process?.printPaperSP || 0,
                  appearance: "",
                  foldingStandardSizeConclusion: InspectionConclusionEnum.None,
                  foldingStandardSizeResult: "",
                  foldingStandardCountConclusion: InspectionConclusionEnum.None,
                  foldingStandardCountResult: "",
                  foldingStandardSize:
                    product?.dimensionWeight?.foldingStandardSize || "",
                  foldingStandardCount:
                    product?.dimensionWeight?.foldingStandardCount || "",
                },
              ]);
            }
          }
        } catch (ex) {
        } finally {
          setRootStoreProp("appFreeze", false);
        }
      }
    },
    [
      cleanItems,
      getControl,
      itemControl,
      setCustomerSelectionIsEmty,
      setOptionItemsForCustomer,
      setOptionItemsForProductFromWorkOrderPo,
      setOptionItemsForProductIsEmty,
      setRootStoreProp,
    ],
  );

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa phiếu kiểm nghiệm</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer label={itemControl.label}>
          {inspectionNoteItems && inspectionNoteItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              controlSpacing={2}
              children={inspectionNoteItems}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
